/**
 * wayai login — OAuth 2.1 + PKCE browser flow (default) or --token fallback
 *
 * Browser login: Opens AuthKit authorize → PKCE flow → JWT + refresh token
 * Token login:   User pastes a way_ scoped token directly
 */

import * as readline from 'node:readline';
import {
  generateState,
  generateCodeVerifier,
  generateCodeChallenge,
  startCallbackServer,
  exchangeCodeForTokens,
  expiresInToIso,
  validateToken,
  getAuthKitDomain,
  getAuthKitClientId,
  OAUTH_CALLBACK_PORT,
} from '../lib/auth.js';
import { writeConfig } from '../lib/config.js';
import { setOAuthTokens, setWayToken, identifyTokenType } from '../lib/token-store.js';
import { nudgeReportActions } from '../lib/report-inbox.js';

function prompt(question: string, defaultValue?: string): Promise<string> {
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  const display = defaultValue ? `${question} [${defaultValue}]: ` : `${question}: `;
  return new Promise((resolve) => {
    rl.question(display, (answer) => {
      rl.close();
      resolve(answer.trim() || defaultValue || '');
    });
  });
}

const DEFAULT_API_URL = 'https://api.wayai.pro';

export function parseApiUrl(args: string[]): string {
  const idx = args.indexOf('--api-url');
  if (idx !== -1 && args[idx + 1]) {
    return args[idx + 1];
  }
  return DEFAULT_API_URL;
}

/**
 * Surfaces the `offline_access` regression at login time instead of ~1h later,
 * when the access token expires and `getAccessToken` throws "no refresh token
 * available". Returns the warning text when AuthKit issued no refresh token (or
 * an empty one), or null when the session is durable.
 */
export function missingRefreshTokenWarning(refreshToken: string | undefined): string | null {
  if (refreshToken) return null;
  return (
    'Warning: no refresh token was issued — this session will expire when the access token does ' +
    '(~1h) and you will need to run `wayai login` again. This usually means the `offline_access` ' +
    'scope was not granted on the AuthKit application.'
  );
}

export async function loginCommand(args: string[]): Promise<void> {
  const useToken = args.includes('--token');
  const apiUrl = parseApiUrl(args);

  if (useToken) {
    await loginWithToken(apiUrl);
  } else {
    await loginWithBrowser(apiUrl);
  }
}

async function loginWithBrowser(apiUrl: string): Promise<void> {
  console.log('\nConnecting to WayAI...');

  const authkitDomain = getAuthKitDomain();
  const clientId = getAuthKitClientId();

  // Generate PKCE parameters
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  // Fixed port so the redirect URI matches what's registered in WorkOS
  const port = OAUTH_CALLBACK_PORT;
  const redirectUri = `http://127.0.0.1:${port}/callback`;

  // Build AuthKit authorization URL with PKCE
  const authorizeUrl = new URL(`${authkitDomain}/oauth2/authorize`);
  authorizeUrl.searchParams.set('client_id', clientId);
  authorizeUrl.searchParams.set('redirect_uri', redirectUri);
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('code_challenge', codeChallenge);
  authorizeUrl.searchParams.set('code_challenge_method', 'S256');
  authorizeUrl.searchParams.set('state', state);
  // offline_access is required for AuthKit to issue a refresh_token alongside the JWT;
  // without it, the CLI would re-prompt login every time the access token expires (~1h).
  authorizeUrl.searchParams.set('scope', 'openid profile email offline_access');

  try {
    // Start callback server before opening browser
    const callbackPromise = startCallbackServer(port, state);

    // Try to open browser
    const openedBrowser = await tryOpenBrowser(authorizeUrl.toString());
    if (!openedBrowser) {
      console.log(`\nOpen this URL in your browser to log in:\n\n  ${authorizeUrl.toString()}\n`);
    } else {
      console.log('Opening browser for login...');
    }

    const { code } = await callbackPromise;

    console.log('Exchanging authorization code...');
    const tokens = await exchangeCodeForTokens(
      authkitDomain,
      clientId,
      code,
      codeVerifier,
      redirectUri,
    );

    // Match the defensive pattern in getAccessToken's refresh path: only persist
    // refresh_token when AuthKit returned one (it's gated on the `offline_access`
    // scope, so a missing value would mean scope config regressed).
    const expiresAt = expiresInToIso(tokens.expires_in);
    await setOAuthTokens(tokens.access_token, tokens.refresh_token, expiresAt);
    writeConfig({ api_url: apiUrl, token_expires_at: expiresAt });

    console.log('\nLogin successful!');
    console.log('Token stored in OS keyring; metadata at ~/.wayai/config.json');

    const refreshWarning = missingRefreshTokenWarning(tokens.refresh_token);
    if (refreshWarning) console.warn(`\n${refreshWarning}`);

    // Best-effort: remind the reporter of any reports awaiting their accept/contest.
    await nudgeReportActions(apiUrl, tokens.access_token);
  } catch (err) {
    console.error(`\nLogin failed: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }
}

async function loginWithToken(apiUrl: string): Promise<void> {
  const token = await prompt('API Token (way_...)');

  if (!token) {
    console.error('Token is required.');
    process.exit(1);
  }

  if (identifyTokenType(token) !== 'way_token') {
    console.error('Invalid token format. Tokens start with "way_".');
    process.exit(1);
  }

  console.log('\nValidating token...');

  try {
    await validateToken(apiUrl, token);
  } catch (err) {
    console.error(`Token validation failed: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  await setWayToken(token);
  writeConfig({ api_url: apiUrl });

  console.log('\nLogin successful! Token stored in OS keyring; metadata at ~/.wayai/config.json');

  // Best-effort: remind the reporter of any reports awaiting their accept/contest.
  await nudgeReportActions(apiUrl, token);
}

async function tryOpenBrowser(url: string): Promise<boolean> {
  if (process.env.SSH_CONNECTION || process.env.SSH_TTY) {
    return false;
  }

  try {
    const { execFile } = await import('node:child_process');
    const platform = process.platform;

    let cmd: string;
    let args: string[];
    if (platform === 'darwin') {
      cmd = 'open';
      args = [url];
    } else if (platform === 'win32') {
      cmd = 'cmd';
      args = ['/c', 'start', '', url];
    } else {
      cmd = 'xdg-open';
      args = [url];
    }

    return new Promise((resolve) => {
      execFile(cmd, args, (err) => resolve(!err));
    });
  } catch {
    return false;
  }
}
